// Pin… and Rollback… (spec/dashboard.md section 12.2; R-BR-04; R-ACC-01 protected environments; Q-C-11, Q-C-14, Q-C-21).
// Both say what the environment runs now and show the field-level Changes the write would make (ReleaseDiff). Pin…
// defaults to the newest release of the branch the environment already runs (the catalog is game-wide, so "the newest
// release" alone could ship another branch) and, on a protected environment, waits for the typed slug. Rollback… names the
// previous default release (from the pin history) and needs a reason, but no typed slug: speed matters in a rollback.
// The server's refusal (an overlap `409` naming the colliding ranges, a manifest not published yet) stays in the dialog.
import { useEffect, useState, type ReactNode } from 'react';
import { ops } from '../../api/operations';
import type { Environment, Page, Release, ReleasePin } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Field';
import { Select, TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { formatInstant, useTimeZone } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { useQuery } from '../../workspace/cache';
import { ReleaseDiff } from './ReleaseDiff';
import { activeDefaultPin, agoText, asApiError, currentRelease, groupReleasesByBranch, keys, newestOfBranch, previousDefaultPin, releaseName, sha8 } from './releases';
import '../org/org.css';
import { AlertCircle, AlertTriangle, ChevronRight } from '../../ui/icons';

// ---- shared reads -----------------------------------------------------------------------------------------------------------

/** The active pins of an environment (the card, the Releases tab's "pinned on" column and the dialogs share the key). */
export function useActivePins(env: string) {
  const { org, game } = useGame();
  const { client } = useAuth();
  return useQuery(keys.pins(org, game.slug, env), () => client.data(ops.listPins, { path: { org, game: game.slug, env }, query: { active: 'true', limit: 100 } }) as Promise<Page<ReleasePin>>);
}

/** Every pin of an environment, superseded and revoked included (the card's history and Rollback… share the key). */
export function usePinHistory(env: string, enabled = true) {
  const { org, game } = useGame();
  const { client } = useAuth();
  return useQuery(enabled ? keys.pinHistory(org, game.slug, env) : null, () => client.data(ops.listPins, { path: { org, game: game.slug, env }, query: { limit: 200 } }) as Promise<Page<ReleasePin>>);
}

// ---- shared pieces ----------------------------------------------------------------------------------------------------------

function useWhen() {
  const [zone] = useTimeZone();
  const { offsets } = useGame();
  return (v: string | null | undefined) => formatInstant(v, zone, { offsets });
}

/** The release select grouped per branch: the branch the environment runs first ("dev · runs here"), then alphabetically. */
export function ReleaseSelect({ releases, value, onChange, runsBranch }: { releases: Release[]; value: string; onChange: (releaseId: string) => void; runsBranch: string | null }) {
  const when = useWhen();
  return (
    <Select aria-label="Release" value={value} onChange={(e) => onChange(e.target.value)}>
      {releases.length === 0 && <option value="">no release built yet</option>}
      {groupReleasesByBranch(releases, runsBranch).map((g) => (
        <optgroup key={g.branch} label={g.branch === runsBranch ? `${g.branch} · runs here` : g.branch}>
          {g.releases.map((r) => (
            <option key={r.releaseId} value={r.releaseId}>
              #{r.number} · {sha8(r.commit)} · {when(r.createdAt)}
            </option>
          ))}
        </optgroup>
      ))}
    </Select>
  );
}

/** "<Env> runs #38 · branch dev · pinned 2d ago by K." or "Nothing is pinned on <Env> yet." */
export function CurrentLine({ env, pin, release, releases }: { env: Environment; pin: ReleasePin | null; release: Release | null; releases: Release[] }) {
  if (!pin) return <p className="pin-current muted small" data-testid="pin-current">Nothing is pinned on {env.name} yet.</p>;
  return (
    <p className="pin-current small" data-testid="pin-current">
      {env.name} runs <strong>{releaseName(pin.releaseId, releases)}</strong>
      {release ? ` · branch ${release.branchName}` : ''} · pinned {agoText(pin.createdAt)}
      {pin.pinnedBy?.label ? ` by ${pin.pinnedBy.label}` : ''}.
    </p>
  );
}

/** The collapsible field-level diff between what the environment runs and the release the write would put there. */
export function ChangesSection({ from, to, defaultOpen, children }: { from: Release | null; to: Release | null; defaultOpen: boolean; children?: ReactNode }) {
  if (!to) return null;
  return (
    <details className="pin-changes" data-testid="pin-changes" open={defaultOpen}>
      <summary>
        <ChevronRight className="icon-s pin-changes-chevron" aria-hidden="true" />
        Changes
      </summary>
      {children}
      <ReleaseDiff branch={to.branchName} fromCommit={from?.commit ?? null} toCommit={to.commit} emptyMessage="The environment already runs this content." />
    </details>
  );
}

/** The typed-slug confirmation of a protected environment (the pattern of pages/settings/GeneralTab.tsx). */
export function TypedConfirm({ env, value, onChange, why }: { env: Environment; value: string; onChange: (v: string) => void; why: string }) {
  return (
    <Field label={`Type ${env.slug} to confirm`} help={why} required>
      <TextInput aria-label={`Type ${env.slug} to confirm`} value={value} onChange={(e) => onChange(e.target.value)} autoComplete="off" />
    </Field>
  );
}

export function Warning({ children }: { children: ReactNode }) {
  return (
    <div className="banner banner-warning pin-warning" role="note">
      <AlertTriangle className="banner-icon" aria-hidden="true" />
      <div className="banner-body">{children}</div>
    </div>
  );
}

export function ServerError({ message }: { message: string | null }) {
  return message ? (
    <div className="banner banner-danger pin-error" role="alert">
      <AlertCircle className="banner-icon" aria-hidden="true" />
      <div className="banner-body">{message}</div>
    </div>
  ) : null;
}

// ---- Pin… ---------------------------------------------------------------------------------------------------------------------

export function PinDialog({
  env,
  releases,
  open,
  onClose,
  onDone,
  missingPermission,
  initialReleaseId,
}: {
  env: Environment;
  releases: Release[];
  open: boolean;
  onClose: () => void;
  onDone: () => void;
  missingPermission: string | null;
  /** preselected release (the `?pin=<env>&release=<id>` deep link); else the newest release of the branch the env runs */
  initialReleaseId?: string;
}) {
  const { org, game } = useGame();
  const { client } = useAuth();
  const toasts = useToasts();
  const pinsQ = useActivePins(env.slug);
  const pin = activeDefaultPin(pinsQ.data?.items);
  const current = currentRelease(pinsQ.data?.items, releases);
  const [releaseId, setReleaseId] = useState('');
  const [touched, setTouched] = useState(false);
  const [mode, setMode] = useState<'default' | 'range'>('default');
  const [min, setMin] = useState('');
  const [max, setMax] = useState('');
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runsBranch = current?.branchName ?? null;
  useEffect(() => {
    if (!open || touched) return;
    const initial = initialReleaseId && releases.some((r) => r.releaseId === initialReleaseId) ? initialReleaseId : null;
    setReleaseId(initial ?? newestOfBranch(releases, runsBranch)?.releaseId ?? releases[0]?.releaseId ?? '');
  }, [open, touched, releases, runsBranch, initialReleaseId]);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const close = () => {
    setMode('default');
    setMin('');
    setMax('');
    setReason('');
    setTyped('');
    setReleaseId('');
    setTouched(false);
    setError(null);
    onClose();
  };

  const chosen = releases.find((r) => r.releaseId === releaseId) ?? null;
  const rangeMissing = mode === 'range' && !min.trim() && !max.trim();
  const unconfirmed = env.isProtected && typed !== env.slug;
  const submit = async () => {
    if (!releaseId || rangeMissing || unconfirmed) return;
    setBusy(true);
    setError(null);
    const body = {
      releaseId,
      ...(mode === 'range' && min.trim() ? { minAppVersion: min.trim() } : {}),
      ...(mode === 'range' && max.trim() ? { maxAppVersion: max.trim() } : {}),
      ...(reason.trim() ? { reason: reason.trim() } : {}),
    };
    try {
      await client.data(ops.pinRelease, { path: { org, game: game.slug, env: env.slug }, body });
      toasts.success(`Pinned ${releaseName(releaseId, releases)} on ${env.name}`);
      onDone();
      close();
    } catch (e) {
      setError(asApiError(e).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Pin a release on ${env.name}`} open={open} onClose={close} size="m">
      <form
        data-testid="pin-dialog"
        className="pin-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <CurrentLine env={env} pin={pin} release={current} releases={releases} />
        <Field label="Release">
          <ReleaseSelect
            releases={releases}
            value={releaseId}
            runsBranch={runsBranch}
            onChange={(id) => {
              setTouched(true);
              setReleaseId(id);
            }}
          />
        </Field>
        {current && chosen && chosen.branchName !== current.branchName && (
          <Warning>
            Switches {env.name} from branch {current.branchName} to branch {chosen.branchName}
          </Warning>
        )}
        <fieldset className="pin-mode">
          <legend>Pin as</legend>
          <label className="check">
            <input type="radio" name="pin-mode" value="default" checked={mode === 'default'} onChange={() => setMode('default')} />
            <span>Default</span>
          </label>
          <label className="check">
            <input type="radio" name="pin-mode" value="range" checked={mode === 'range'} onChange={() => setMode('range')} />
            <span>App-version range</span>
          </label>
        </fieldset>
        {mode === 'range' && (
          <div className="form-grid pin-range">
            <Field label="Min app version">
              <TextInput aria-label="Min app version" placeholder="1.0.0" value={min} onChange={(e) => setMin(e.target.value)} autoComplete="off" />
            </Field>
            <Field label="Max app version">
              <TextInput aria-label="Max app version" placeholder="1.9.9" value={max} onChange={(e) => setMax(e.target.value)} autoComplete="off" />
            </Field>
          </div>
        )}
        <ChangesSection from={current} to={chosen} defaultOpen={Boolean(pin)} />
        <Field label="Reason">
          <TextInput aria-label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {env.isProtected && <TypedConfirm env={env} value={typed} onChange={setTyped} why={`${env.name} is protected: pins here reach players at once.`} />}
        <ServerError message={error} />
        <div className="modal-footer form-footer">
          <Button onClick={close}>Cancel</Button>
          <Button
            type="submit"
            variant="primary"
            busy={busy}
            disabled={!releaseId || rangeMissing || unconfirmed}
            missingPermission={missingPermission}
            title={rangeMissing ? 'Give a min or a max app version' : unconfirmed ? `Type ${env.slug} to confirm` : undefined}
          >
            Pin
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ---- Rollback… ----------------------------------------------------------------------------------------------------------------

export function RollbackDialog({ env, releases, open, onClose, onDone, missingPermission }: { env: Environment; releases: Release[]; open: boolean; onClose: () => void; onDone: () => void; missingPermission: string | null }) {
  const { org, game } = useGame();
  const { client } = useAuth();
  const toasts = useToasts();
  const pinsQ = useActivePins(env.slug);
  const historyQ = usePinHistory(env.slug, open);
  const current = currentRelease(pinsQ.data?.items, releases);
  const previous = previousDefaultPin(historyQ.data?.items);
  const previousRelease = previous ? (releases.find((r) => r.releaseId === previous.releaseId) ?? null) : null;
  const noPrevious = Boolean(historyQ.data) && !previous;
  const [target, setTarget] = useState<'previous' | 'chosen'>('previous');
  const [releaseId, setReleaseId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReleaseId((cur) => cur || newestOfBranch(releases, current?.branchName)?.releaseId || releases[0]?.releaseId || '');
      setError(null);
    }
  }, [open, releases, current?.branchName]);

  const effectiveTarget = noPrevious ? 'chosen' : target;

  const close = () => {
    setTarget('previous');
    setReleaseId('');
    setReason('');
    setError(null);
    onClose();
  };

  const submit = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    setError(null);
    const body = { ...(effectiveTarget === 'chosen' && releaseId ? { releaseId } : {}), reason: reason.trim() };
    try {
      const pin = await client.data(ops.rollbackPin, { path: { org, game: game.slug, env: env.slug }, body });
      const to = (pin as { releaseId?: string } | undefined)?.releaseId;
      toasts.success(to ? `Rolled ${env.name} back to ${releaseName(to, releases)}` : `Rolled ${env.name} back`);
      onDone();
      close();
    } catch (e) {
      setError(asApiError(e).message);
    } finally {
      setBusy(false);
    }
  };

  const chosen = effectiveTarget === 'chosen' ? (releases.find((r) => r.releaseId === releaseId) ?? null) : previousRelease;
  const previousLine = previous ? (
    <>
      — <strong>{releaseName(previous.releaseId, releases)}</strong>
      {previousRelease ? ` · branch ${previousRelease.branchName}` : ''} · pinned {agoText(previous.createdAt)}
      {previous.pinnedBy?.label ? ` by ${previous.pinnedBy.label}` : ''}.
    </>
  ) : noPrevious ? (
    `No earlier default pin on ${env.name}`
  ) : historyQ.error ? (
    asApiError(historyQ.error).message
  ) : (
    'looking it up…'
  );

  return (
    <Modal title={`Roll back ${env.name}`} open={open} onClose={close} size="m">
      <form
        data-testid="rollback-dialog"
        className="pin-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <p className="muted small">Re-pins the default in seconds, no rebuild.</p>
        <CurrentLine env={env} pin={activeDefaultPin(pinsQ.data?.items)} release={current} releases={releases} />
        {env.isProtected && <Warning>{env.name} is protected: the rollback reaches players at once.</Warning>}
        <fieldset className="pin-mode pin-mode-stacked">
          <legend>Roll back to</legend>
          <div className="pin-option">
            <label className="check">
              <input type="radio" name="rollback-target" value="previous" checked={effectiveTarget === 'previous'} disabled={noPrevious} onChange={() => setTarget('previous')} aria-describedby={`rollback-previous-${env.slug}`} />
              <span>The previous default release</span>
            </label>
            <span id={`rollback-previous-${env.slug}`} className="muted small" data-testid="rollback-previous">
              {previousLine}
            </span>
          </div>
          <div className="pin-option">
            <label className="check">
              <input type="radio" name="rollback-target" value="chosen" checked={effectiveTarget === 'chosen'} onChange={() => setTarget('chosen')} />
              <span>A chosen release</span>
            </label>
          </div>
        </fieldset>
        {effectiveTarget === 'chosen' && (
          <Field label="Release">
            <ReleaseSelect releases={releases} value={releaseId} onChange={setReleaseId} runsBranch={current?.branchName ?? null} />
          </Field>
        )}
        <ChangesSection from={current} to={chosen} defaultOpen />
        <Field label="Reason" required>
          <TextInput aria-label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <ServerError message={error} />
        <div className="modal-footer form-footer">
          <Button onClick={close}>Cancel</Button>
          <Button
            type="submit"
            variant="danger"
            busy={busy}
            disabled={(effectiveTarget === 'chosen' && !releaseId) || !reason.trim()}
            missingPermission={missingPermission}
            title={!reason.trim() ? 'Give a reason' : undefined}
          >
            Roll back
          </Button>
        </div>
      </form>
    </Modal>
  );
}
